import {
  Trash2, Warehouse, Truck, Plus, ArrowRight, Loader2,
} from 'lucide-react';
import type {
  CallOff, CallOffLine, AvailablePartForCallOff, CallOffDeliveryDestination,
} from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { plainDecimal } from '../../../lib/plainDecimal';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';

interface InlineEditChange {
  lineId: number;
  partNumber: string;
  oldQty: string;
  newQty: string;
}

interface CallOffLinesTableProps {
  callOff: CallOff;
  inlineEditMode: boolean;
  inlineEditChanges: Record<number, InlineEditChange>;
  editingLineId: number | null;
  editingField: string | null;
  editValue: string;
  savingEdit: boolean;
  supplierCanEditLines: boolean;
  onQuantityChange: (line: CallOffLine, newQty: string) => void;
  onStartLineEdit: (lineId: number, field: string, currentValue: string) => void;
  onSaveLineEdit: () => void;
  onCancelLineEdit: () => void;
  setEditValue: (v: string) => void;
  removeLineItem: (line: CallOffLine) => void;
  /** Add parts state */
  showAddParts: boolean;
  availableParts: AvailablePartForCallOff[];
  availableDeliveries: CallOffDeliveryDestination[];
  selectedDelivery: string;
  setSelectedDelivery: (v: string) => void;
  addPartsLoading: boolean;
  partQuantities: Record<number, number>;
  setPartQuantities: React.Dispatch<React.SetStateAction<Record<number, number>>>;
  addingPart: boolean;
  handleAddPart: (part: AvailablePartForCallOff) => void;
  isSupplier: boolean;
  /** Per-part PO availability budget. When a warehouse line's part is over,
   *  the qty input goes red and the parent disables Save Changes. */
  overByPart?: Record<string, { effective: number; consumed: number; over: boolean }>;
}

/**
 * Lines table for the call-off detail modal.
 * Groups lines by delivery type (warehouse / direct) with section headers.
 */
export default function CallOffLinesTable({
  callOff, inlineEditMode, inlineEditChanges,
  editingLineId: _editingLineId, editingField: _editingField,
  editValue: _editValue, savingEdit: _savingEdit,
  supplierCanEditLines: _supplierCanEditLines,
  onQuantityChange,
  onStartLineEdit: _onStartLineEdit, onSaveLineEdit: _onSaveLineEdit,
  onCancelLineEdit: _onCancelLineEdit, setEditValue: _setEditValue,
  removeLineItem,
  showAddParts, availableParts, availableDeliveries,
  selectedDelivery, setSelectedDelivery,
  addPartsLoading, partQuantities, setPartQuantities, addingPart, handleAddPart,
  isSupplier: _isSupplier,
  overByPart,
}: CallOffLinesTableProps) {
  const { user } = useAuth();
  // CR-7194: price is for the buying side, the supplier and the factory. A
  // lorry driver / logistics user gets the delivery document only.
  const canSeePrice = user?.company_type !== 'logistics';
  const warehouseLines = callOff.lines.filter((l: CallOffLine) => l.delivery_type === 'warehouse');
  const directLines = callOff.lines.filter((l: CallOffLine) => l.delivery_type !== 'warehouse');
  // CR-6173: the Pallets column is now shown to every role (customer / supplier
  // / logistics), so it is always part of the base column count.
  // CR-6291: +2 for the Unit Wt and Line Wt columns.
  // CR-7142: +1 for the Sales Order column.
  // CR-7194: +2 for Price / Unit and Line Total, which a logistics user never
  // sees — they handle the delivery, not the commercial terms. The backend
  // strips both fields for that role too, so this only hides empty columns.
  const colSpan = 11 + (canSeePrice ? 2 : 0) + (inlineEditMode ? 1 : 0);

  // The qty in play for a line — the in-progress batch-edit value when one is
  // being edited, otherwise the requested qty — so pallet/weight totals stay live.
  const qtyForLine = (line: CallOffLine): number => {
    const change = inlineEditChanges[line.id];
    return parseFloat(change ? change.newQty : line.requested_qty) || 0;
  };

  // Pallets per line for the logistics view. CR-6173: the haulier books whole
  // pallets, so round up to the next whole pallet — a part-filled pallet still
  // occupies a full pallet space.
  const palletsForLine = (line: CallOffLine): number => {
    const palletQty = parseFloat(line.pallet_qty) || 0;
    return palletQty > 0 ? Math.ceil(qtyForLine(line) / palletQty) : 0;
  };

  // CR-6291: per-line gross weight = qty × unit weight (kg) from the Parts Master.
  const weightForLine = (line: CallOffLine): number => {
    const unit = parseFloat(line.unit_weight_kg ?? '') || 0;
    return qtyForLine(line) * unit;
  };

  // CR-7194: line total = price per unit × the qty being released, so it tracks
  // an in-progress inline edit rather than showing the stored line_total (which
  // is computed against the originally requested qty).
  const priceForLine = (line: CallOffLine): number => parseFloat(line.unit_price ?? '') || 0;
  const lineTotalForLine = (line: CallOffLine): number => priceForLine(line) * qtyForLine(line);
  // An unpriced part is a real state — no price fed from the ERP yet.
  const hasPrice = (line: CallOffLine): boolean =>
    line.unit_price != null && line.unit_price !== '';

  const warehousePalletTotals = warehouseLines.reduce<Record<string, number>>((acc, line) => {
    const dest = line.delivery_destination_name || 'Warehouse';
    acc[dest] = (acc[dest] || 0) + palletsForLine(line);
    return acc;
  }, {});
  const directPalletTotal = directLines.reduce((sum, line) => sum + palletsForLine(line), 0);
  const grandPalletTotal =
    Object.values(warehousePalletTotals).reduce((a, b) => a + b, 0) + directPalletTotal;
  // CR-6291: total gross weight for the whole load/lorry.
  const grandWeightTotal = callOff.lines.reduce((sum, line) => sum + weightForLine(line), 0);

  const renderLine = (line: CallOffLine) => {
    const batchChange = inlineEditChanges[line.id];
    const displayQty = batchChange ? batchChange.newQty : line.requested_qty;
    const loadQty = parseFloat(line.load_qty) || 0;
    const lineQty = parseFloat(displayQty) || 0;
    const lineFill = loadQty > 0 ? Math.round((lineQty / loadQty) * 100) : 0;
    const palletQty = parseFloat(line.pallet_qty) || 0;
    const pallets = palletQty > 0 ? palletsForLine(line) : '—';
    const unitWeight = parseFloat(line.unit_weight_kg ?? '') || 0;
    // Exactly as the data carries it — a 0.0045 kg part x 3 reads 0.0135, not 0.01.
    const lineWeightLabel = unitWeight > 0 ? plainDecimal(weightForLine(line)) : '—';
    const partInfo = line.delivery_type === 'warehouse' ? overByPart?.[line.part_number] : undefined;
    const isOver = !!partInfo?.over;

    return (
      <TableRow key={line.id} className={isOver ? 'bg-red-50' : (batchChange ? 'bg-amber-50' : '')}>
        <TableCell className="font-mono text-xs font-medium">{line.part_number}</TableCell>
        <TableCell className="text-sm">{line.description}</TableCell>
        <TableCell>
          {line.delivery_type === 'warehouse'
            ? line.delivery_destination_name || 'Warehouse'
            : line.delivery_address || line.sales_order || 'Direct'}
        </TableCell>
        <TableCell className="font-mono text-xs">
          {line.sales_order || '—'}
        </TableCell>
        <TableCell className="text-right">
          {inlineEditMode ? (
            <div className="flex flex-col items-end gap-0.5">
              <Input
                type="number"
                className={`h-7 w-20 text-right text-sm ${
                  isOver ? 'border-red-500 bg-red-50 text-red-700 focus-visible:ring-red-500' : ''
                }`}
                value={displayQty}
                onChange={(e) => onQuantityChange(line, e.target.value)}
                min={0}
              />
              {isOver && partInfo && (
                <span className="text-[10px] text-red-700 whitespace-nowrap">
                  {partInfo.effective === 0
                    ? 'No PO available'
                    : `Only ${partInfo.effective} available`}
                </span>
              )}
            </div>
          ) : (
            <span>{line.requested_qty}</span>
          )}
        </TableCell>
        <TableCell className="text-right">{line.load_qty}</TableCell>
        {/* CR-7194: price per unit and its line total. */}
        {canSeePrice && (
          <TableCell className="text-right">
            {hasPrice(line) ? priceForLine(line).toFixed(2) : '—'}
          </TableCell>
        )}
        {canSeePrice && (
          <TableCell className="text-right font-medium">
            {hasPrice(line) ? lineTotalForLine(line).toFixed(2) : '—'}
          </TableCell>
        )}
        <TableCell className="text-right font-medium">{pallets}</TableCell>
        {/* CR-6291: gross weight per product + qty × unit line total (kg). */}
        <TableCell className="text-right">{unitWeight > 0 ? plainDecimal(line.unit_weight_kg) : '—'}</TableCell>
        <TableCell className="text-right font-medium">{lineWeightLabel}</TableCell>
        <TableCell className="text-right">
          <Badge
            variant="secondary"
            className={lineFill >= 100 ? 'bg-green-100 text-green-700' : lineFill >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}
          >
            {lineFill}%
          </Badge>
        </TableCell>
        <TableCell>
          <span className="text-xs text-muted-foreground">{line.notes || ''}</span>
        </TableCell>
        {inlineEditMode && (
          <TableCell className="text-center">
            <Button variant="ghost" size="sm" className="h-7 text-red-500 hover:text-red-700" onClick={() => removeLineItem(line)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TableCell>
        )}
      </TableRow>
    );
  };

  return (
    <>
      {callOff.lines && callOff.lines.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Part Number</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Destination</TableHead>
              {/* CR-7142: the supplier's sales order, so the warehouse knows
                  which PO line to take the stock off. It was reachable only on
                  the ASN detail before. */}
              <TableHead>Sales Order</TableHead>
              <TableHead className="text-right">Requested</TableHead>
                            <TableHead className="text-right">Load Qty</TableHead>
              {canSeePrice && <TableHead className="text-right">Price / Unit</TableHead>}
              {canSeePrice && <TableHead className="text-right">Line Total</TableHead>}
              <TableHead className="text-right">Pallets</TableHead>
              <TableHead className="text-right">Unit Wt (kg)</TableHead>
              <TableHead className="text-right">Line Wt (kg)</TableHead>
              <TableHead className="text-right">Fill %</TableHead>
              <TableHead>Notes</TableHead>
              {inlineEditMode && <TableHead className="text-center">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {warehouseLines.length > 0 && (
              <>
                <TableRow className="bg-red-50 hover:bg-red-50">
                  <TableCell colSpan={colSpan} className="py-2">
                    <div className="flex items-center gap-1.5 font-semibold text-red-700 text-xs uppercase tracking-wide">
                      <Warehouse className="h-3.5 w-3.5" />
                      Warehouse Delivery ({warehouseLines.length} items)
                    </div>
                  </TableCell>
                </TableRow>
                {warehouseLines.map(renderLine)}
              </>
            )}
            {directLines.length > 0 && (
              <>
                <TableRow className="bg-blue-50 hover:bg-blue-50">
                  <TableCell colSpan={colSpan} className="py-2">
                    <div className="flex items-center gap-1.5 font-semibold text-blue-700 text-xs uppercase tracking-wide">
                      <Truck className="h-3.5 w-3.5" />
                      Direct Delivery ({directLines.length} items)
                    </div>
                  </TableCell>
                </TableRow>
                {directLines.map(renderLine)}
              </>
            )}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-8 text-muted-foreground">No lines.</div>
      )}

      {/* CR-6173/CR-6291: pallet + gross weight totals — shown to all roles. */}
      {callOff.lines && callOff.lines.length > 0 && (
        <Card className="mt-4 border-blue-200">
          <CardHeader className="py-2 px-4 bg-blue-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <Truck className="h-4 w-4" />
              Pallet &amp; Weight Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-1.5 text-sm">
              {Object.entries(warehousePalletTotals).map(([dest, total]) => (
                <div key={dest} className="flex justify-between">
                  <span className="text-muted-foreground">{dest}</span>
                  <span className="font-medium">{total} pallets</span>
                </div>
              ))}
              {directPalletTotal > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Direct Deliveries</span>
                  <span className="font-medium">{directPalletTotal} pallets</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-1.5 mt-1.5 font-semibold">
                <span>Total Pallets</span>
                <span>{grandPalletTotal} pallets</span>
              </div>
              {/* CR-6291: total gross weight for the whole load/lorry. */}
              <div className="flex justify-between font-semibold">
                <span>Total Gross Weight</span>
                <span>{plainDecimal(grandWeightTotal)} kg</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Supplier Add Parts Section */}
      {inlineEditMode && showAddParts && (
        <Card className="mt-4 border-blue-200">
          <CardHeader className="py-3 px-4 bg-blue-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add New Parts
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 p-2 rounded bg-blue-50 text-blue-700 text-xs">
              <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              Select a warehouse destination, then add products with quantities.
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Warehouse Destination</Label>
              <Select value={selectedDelivery} onValueChange={setSelectedDelivery}>
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Select warehouse..." />
                </SelectTrigger>
                <SelectContent>
                  {availableDeliveries
                    .map((d, idx) => ({ d, idx }))
                    .filter(({ d }) => d.delivery_type === 'warehouse')
                    .map(({ d, idx }) => (
                      <SelectItem key={idx} value={String(idx)}>
                        {d.delivery_destination_name || 'Warehouse'}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {addPartsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : availableParts.length > 0 ? (
              <div className="max-h-[250px] overflow-y-auto border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Part No</TableHead>
                      <TableHead className="text-xs">Description</TableHead>
                      <TableHead className="text-xs text-right">Load Qty</TableHead>
                      <TableHead className="text-xs text-right w-[80px]">Qty</TableHead>
                      <TableHead className="text-xs text-center w-[70px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {availableParts.map(part => (
                      <TableRow key={part.id}>
                        <TableCell className="font-mono text-xs">{part.part_number}</TableCell>
                        <TableCell className="text-xs">{part.description}</TableCell>
                        <TableCell className="text-xs text-right">{part.load_qty}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            className="h-6 w-[70px] text-right text-xs ml-auto"
                            min={0}
                            value={partQuantities[part.id] || ''}
                            onChange={(e) => setPartQuantities(prev => ({
                              ...prev,
                              [part.id]: parseFloat(e.target.value) || 0,
                            }))}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-xs px-2"
                            disabled={addingPart || !selectedDelivery || !(partQuantities[part.id] > 0)}
                            onClick={() => handleAddPart(part)}
                          >
                            Add
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-3">
                No additional parts available to add.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {callOff.notes && (
        <Card className="mt-4">
          <CardHeader className="py-2 px-4">
            <CardTitle className="text-sm">Notes</CardTitle>
          </CardHeader>
          <CardContent className="py-2 px-4 text-sm">{callOff.notes}</CardContent>
        </Card>
      )}
    </>
  );
}
